import React from 'react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import Icon from '../../../../app/icon/Icon'
import UseUserType from '../../../../hooks/UseUserType'
import { type PortalBitrixRequest } from '../../../../functions/shared/orders/portalBitrixRequests'
import formatPrice from '../../../../functions/shared/format/formatPrice'

interface PortalRequestCardProps {
    request: PortalBitrixRequest
}

export default function PortalRequestCard({ request }: PortalRequestCardProps): JSX.Element {
    const { userTypeLowercase } = UseUserType()
    const isViewing = request.requestType === 'viewing'
    const borderColor = isViewing ? 'primary' : 'success'
    const bgColor = isViewing ? 'primary' : 'success'
    const typeLabel = isViewing ? 'Viewing Request' : 'Offer Request'
    const createdLabel = dayjs(request.createdAt).isValid() ? dayjs(request.createdAt).format('DD MMM YYYY, HH:mm') : request.createdAt
    const offerNumber = Number(request.offerAmount)
    const offerAmountLabel = Number.isFinite(offerNumber) ? `${formatPrice(offerNumber)} AED` : request.offerAmount

    return (
        <div className={`card shadow-sm my-2 p-1 me-1 position-relative border border-1 border-${borderColor} bg-light-${bgColor}`}>
            <div className="d-flex flex-row row g-0 align-items-start justify-content-between">
                <div className="d-flex flex-column col-8 p-2">
                    <span className={`position-absolute badge badge-light-${borderColor} border border-${borderColor} text-${borderColor} p-1`} style={{ top: '-7px', left: '-5px' }}>
                        {typeLabel}
                    </span>

                    <span className="fs-6 text-gray-800 d-flex align-items-center gap-2">
                        <Icon name={isViewing ? 'visibility' : 'monetization_on'} styles={{ minWidth: '12.9px' }} />
                        <span className="text-dots-app fw-semibold">{request.unitLabel}</span>
                    </span>

                    <span className="fs-6 text-gray-800 d-flex align-items-center gap-2">
                        <Icon name={'person'} styles={{ minWidth: '12.9px' }} />
                        <div className="">
                            <span className="text-dots-app">{request.requesterName}</span>
                            <div className="fs-8 text-gray-700 my-n2">{request.requesterEmail}</div>
                        </div>
                    </span>

                    <span className="fs-6 text-gray-800 d-flex align-items-center gap-2">
                        <Icon name={'location_on'} styles={{ minWidth: '12.9px' }} />
                        <span className="text-dots-app">{request.propertyLabel}</span>
                    </span>

                    <div className="d-flex align-items-center gap-2 mt-2">
                        {isViewing
                            ? <span className="badge badge-light-primary border border-primary text-primary fs-8">{`Preferred: ${request.preferredTime}`}</span>
                            : <span className="badge badge-light-success border border-success text-success fs-8">{`Offer: ${offerAmountLabel}`}</span>}
                    </div>
                </div>

                <div className="d-flex flex-column col-4 p-1 text-end">
                    <div className="fs-1x text-dark lh-1 fw-bold">
                        <span className={`me-1 text-end badge h-10px min-w-10px w-10px badge-circle badge-outline badge-${borderColor} bg-light-${bgColor}`}> </span>
                        {request.statusLabel}
                    </div>

                    <div className="fs-8 text-gray-700 pt-1">
                        {createdLabel}
                    </div>

                    <Link
                        to={`/${userTypeLowercase}/portal_request_actions/${String(request.orderId)}`}
                        className="text-decoration-none mt-2"
                    >
                        <div className={`fs-7 pt-1 badge border border-${borderColor}`}>
                            GET INFO
                            <Icon name={'expand_more'} className={'text-dark fs-2 ms-1'} useWrapper />
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    )
}
